import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Mail } from "lucide-react";
import { useTranslation } from 'react-i18next';

const AboutSection = () => {
  const { t } = useTranslation();
  
  return (
    <section id="about" className="py-20 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-5xl font-bold mb-4 text-glow-cyber">
                <span className="bg-gradient-cyber bg-clip-text text-transparent">
                  {t('about.name')}
                </span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                <span className="text-cyber-blue">Ing.</span> {t('about.role')}
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {t('about.intro')}
              </p>
              
              <p className="text-muted-foreground leading-relaxed">
                {t('about.experience')}
              </p>

              <p className="text-muted-foreground leading-relaxed">
                <span className="text-cyber-green font-semibold">{t('about.coreValues')}</span> {t('about.values')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-cyber text-primary-foreground hover-glow border-0 px-6 py-3"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('about.downloadCV')}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="glass border-cyber-blue text-cyber-blue hover:bg-cyber-blue/10 px-6 py-3"
              >
                <Mail className="w-5 h-5 mr-2" />
                {t('about.getInTouch')}
              </Button>
            </div>
          </div>

          {/* Skills Card */}
          <Card className="glass p-8 border-glow hover-glow">
            <h3 className="text-2xl font-bold mb-6 text-glow-cyber">{t('about.coreTechnologies')}</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-cyber-blue mb-3 uppercase tracking-wider">
                  {t('about.microsoftTech')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['C#', '.NET MAUI', 'Blazor', 'WPF', 'MS SQL Server'].map((tech) => (
                    <span key={tech} className="px-3 py-1 bg-cyber-blue/10 text-cyber-blue rounded-full text-sm border border-cyber-blue/20">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-cyber-green mb-3 uppercase tracking-wider">
                  {t('about.webMobile')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['Angular', 'HTML', 'Android Development', 'Full Stack'].map((tech) => (
                    <span key={tech} className="px-3 py-1 bg-cyber-green/10 text-cyber-green rounded-full text-sm border border-cyber-green/20">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-cyber-purple mb-3 uppercase tracking-wider">
                  {t('about.systemsHardware')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['Linux', 'Windows', 'PC Building', 'Home Automation', 'IoT'].map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-cyber-purple/10 text-cyber-purple rounded-full text-sm border border-cyber-purple/20">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-cyber-orange mb-3 uppercase tracking-wider">
                  {t('about.leadership')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['Scrum Master', 'Team Leadership', 'Strategic Planning', 'Problem Solving'].map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-cyber-orange/10 text-cyber-orange rounded-full text-sm border border-cyber-orange/20">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;